/**
 * The rules behind the Landmines accordion, kept out of the component so they
 * can be pinned by tests. Mirrors `LandmineBarLogic.swift` in
 * salesguru-desktop-macos; the WPF bar follows the same rules.
 *
 * Why an accordion at all: landmines used to arrive as corner cards, and with
 * the detector's false-positive rate a rep on a live call got a stream of
 * interruptions. The accordion keeps every landmine (nothing is dismissed and
 * lost) while the only interruption is a short fly-in strip plus a red header
 * that stays red until the rep opens the list.
 *
 * ORDER IS ARRIVAL ORDER. The slice appends, and the bar renders the array
 * as-is. There is deliberately no timestamp sort: an earlier version sorted by
 * `detected_at` and still showed new landmines at the top in testing, and
 * arrival order needs no timestamp at all — it is the order the rep lived through.
 * Rows are identified by their index in that array, which is stable because
 * landmines are only ever appended (or all cleared).
 */

import { ALERT_SPEAKER_COPY, AlertRaiser, AlertViewer, isViewersOwnAlert } from '../constants/alertTheme';

// MARK: attention (the red header and the fly-in strip)

export interface LandmineBarAttention {
  /** Landmines that arrived while the list was closed. The header stays red
   *  while this is non-zero — a cue that fades on a timer is a cue a rep who
   *  looked away for five seconds never sees. */
  unseenCount: number;
}

export interface LandmineCountChange {
  attention: LandmineBarAttention;
  /** True when the fly-in strip should play: only for new LIVE arrivals, and
   *  only while the list is closed (an open list already shows the new card). */
  shouldFlyIn: boolean;
}

export const INITIAL_LANDMINE_BAR_ATTENTION: LandmineBarAttention = { unseenCount: 0 };

export const isLandmineBarAlerting = (attention: LandmineBarAttention): boolean =>
  attention.unseenCount > 0;

/**
 * `arrivedLive` is false for landmines restored from the session (page-load
 * resume or watch-join): they were said before this client attached, so they
 * join the list silently — no strip, and they never count as unseen.
 */
export function landmineCountChanged(
  attention: LandmineBarAttention,
  oldCount: number,
  newCount: number,
  isExpanded: boolean,
  arrivedLive: boolean
): LandmineCountChange {
  if (newCount <= oldCount) {
    // A session reset empties the list; a stale red header must not survive it.
    const next = newCount === 0 ? INITIAL_LANDMINE_BAR_ATTENTION : attention;
    return { attention: next, shouldFlyIn: false };
  }
  if (isExpanded || !arrivedLive) return { attention, shouldFlyIn: false };
  return {
    attention: { unseenCount: attention.unseenCount + (newCount - oldCount) },
    shouldFlyIn: true,
  };
}

export const markLandminesSeen = (): LandmineBarAttention => INITIAL_LANDMINE_BAR_ATTENTION;

// MARK: per-landmine expansion inside the opened list

/** Indices (into the arrival-ordered array) of the rows showing their full card. */
export type LandmineListExpansion = ReadonlySet<number>;

/** Opening the list shows only the newest landmine in full; the rest are one-line rows. */
export function expansionOnOpen(count: number): LandmineListExpansion {
  return count > 0 ? new Set([count - 1]) : new Set();
}

/**
 * While the list is open, a new landmine arrives expanded and older rows keep
 * whatever the rep left them as. A shrink (only ever a session reset) drops
 * rows that no longer exist.
 */
export function expansionOnCountChange(
  expansion: LandmineListExpansion,
  oldCount: number,
  newCount: number
): LandmineListExpansion {
  if (newCount < oldCount) {
    return new Set(Array.from(expansion).filter((index) => index < newCount));
  }
  const next = new Set(expansion);
  for (let index = oldCount; index < newCount; index += 1) next.add(index);
  return next;
}

export function toggleExpansion(expansion: LandmineListExpansion, index: number): LandmineListExpansion {
  const next = new Set(expansion);
  if (next.has(index)) next.delete(index);
  else next.add(index);
  return next;
}

// MARK: speaker wording

/**
 * The speaker word for the Landmines header: "You:" for the viewer's own
 * landmine, else the raiser's name ("Dana:"), else the role ("Customer:"),
 * falling back to "Latest:". Null when nothing says who raised it, so the
 * caller picks the fallback. Same rule as the cards' `speakerLabel`; mirrors
 * `landmineSpeakerWord` on macOS and `SpeakerWord` on WPF.
 */
export function landmineSpeakerPrefix(raiser: AlertRaiser | null | undefined, viewer: AlertViewer): string | null {
  if (isViewersOwnAlert(raiser, viewer)) return ALERT_SPEAKER_COPY.you;
  const speakerName = raiser?.speakerName?.trim();
  if (speakerName) return speakerName;
  if (raiser?.speaker === 'customer') return ALERT_SPEAKER_COPY.customer;
  if (raiser?.speaker === 'salesperson') return ALERT_SPEAKER_COPY.salesperson;
  return null;
}
